import { Injectable, computed, inject, signal } from '@angular/core';
import { ApiClientService } from '@xbh/common-lib';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { parseYyyyMMddToDate } from '../../pages/common/utils/date-utils';
import {
  Fh0001DiagnosticResultsRequest,
  Fh0001DiagnosticResultsResponse,
  Fh0003PercentageChangeResponse,
  Fh0004MasterDataResponse,
  Fh0005PriceTrendResponse,
  Fh0005RiskReturnRequest,
  Fh0005RiskReturnResponse,
  Fh0006ValueTrendRequest,
  Fh0006ValueTrendResponse,
  Fh0020QuestionMasterItem,
  PortfolioInfoInitialItem,
  SeparateAccountBasicItem,
  SeparateAccountDetailItem,
  WordExplanationItem,
} from './fsthp-master-data.entity';

/**
 * マスタデータグローバルストア（fsthp用）
 *
 * FH0010の初期表示処理でマスタデータ取得API（FH0004）・騰落率データ取得API（FH0003）を呼び出し、
 * 全画面で共有するデータを管理する。providedIn: 'root' によりシングルトンとして動作し、
 * ページ遷移後もデータが保持される。
 */
@Injectable({ providedIn: 'root' })
export class FsthpMasterDataStore {
  private readonly apiClient = inject(ApiClientService);

  /** マスタデータ本体（未取得時はnull） */
  private readonly _masterData = signal<Fh0004MasterDataResponse | null>(null);
  /** 騰落率データ本体（未取得時はnull） */
  private readonly _percentageChangeData = signal<Fh0003PercentageChangeResponse | null>(null);

  /** 読み取り専用のマスタデータ */
  readonly masterData = this._masterData.asReadonly();
  /** 読み取り専用の騰落率データ */
  readonly percentageChangeData = this._percentageChangeData.asReadonly();

  /** 投資スタイル診断（FH0020）の回答No（Q1～Q4がすべて揃っている場合のみ保持） */
  private readonly _diagnosisAnswers = signal<Record<number, string> | null>(null);
  /** 診断結果取得API（FH0001）で判定したタイプID（"01"/"02"/"03"） */
  private readonly _diagnosisTypeId = signal<string | null>(null);
  /** FH0021で選択中だったタイプタブのタイプID（"01"/"02"/"03"）。画面離脱時に保持する */
  private readonly _selectedTypeTabId = signal<string | null>(null);
  /**
   * FH0020で診断完了した直後のFH0021初期表示で、診断結果タイプを優先適用するためのフラグ。
   * FH0021で一度参照したら消費（false）する。
   */
  private readonly _pendingDiagnosisResultTab = signal(false);
  /** ご留意事項（FH0011）の「同意する」チェック状態 */
  private readonly _noticeAgreed = signal(false);

  /** マスタデータ取得済みフラグ */
  readonly isLoaded = computed(() => this._masterData() !== null);

  /** 診断回答（Q1～Q4）保持済みフラグ */
  readonly hasDiagnosisAnswers = computed(() => this._diagnosisAnswers() !== null);

  /**
   * マスタデータ・騰落率データを取得してストアに保存する（FH0010初期表示で一度だけ呼び出す）
   *
   * @param onError API異常時に呼び出すコールバック（任意）
   */
  load(onError?: (error: unknown) => void): void {
    this.fetchMasterData().subscribe({
      next: (data) => {
        if (data.resInfo?.status && data.resInfo.status !== 'OK') {
          onError?.(data);
          this._masterData.set({ questionList: [], transitionMaster: [] });
          return;
        }
        this._masterData.set(data);
      },
      error: (error: unknown) => {
        onError?.(error);
        // マスタデータ取得失敗時は空データで初期化
        this._masterData.set({ questionList: [], transitionMaster: [] });
      },
    });

    this.fetchPercentageChangeData().subscribe({
      next: (data) => {
        if (data.resInfo?.status && data.resInfo.status !== 'OK') {
          onError?.(data);
          this._percentageChangeData.set({});
          return;
        }
        this._percentageChangeData.set(data);
      },
      error: (error: unknown) => {
        onError?.(error);
        this._percentageChangeData.set({});
      },
    });
  }

  /**
   * 投資スタイル診断（FH0020）の設問内容リストを取得する。
   * マスタデータ取得API（FH0004）のレスポンスは設問No×回答Noの組み合わせでフラットな1件ずつ返却されるため、
   * 設問No単位でグルーピングし、回答一覧（回答No・回答内容）を持つ形式に変換する。
   */
  getQuestionContentList(): Fh0020QuestionMasterItem[] {
    const questionList = this._masterData()?.questionList ?? [];
    const questionMap = new Map<number, Fh0020QuestionMasterItem>();

    for (const item of questionList) {
      const questionNo = Number(item.questionNumber);
      if (!questionMap.has(questionNo)) {
        questionMap.set(questionNo, { questionNo, questionContent: item.question, answerList: [] });
      }
      questionMap.get(questionNo)!.answerList.push({ answerNo: item.answerNumber, answerContent: item.answer });
    }

    return Array.from(questionMap.values());
  }

  /**
   * 遷移先コードに対応する遷移先の表示タイトルを取得する
   *
   * @param code 遷移先コード
   * @returns 遷移先の表示タイトル（見つからない場合は空文字）
   */
  getDisplayTitle(code: string) {
    return this._masterData()?.transitionMaster?.find((d) => d.transitionDestinationCode?.trim().toLowerCase() === code.trim().toLowerCase())?.displayTitle ?? '';
  }
  /**
   * 遷移先コードに対応する遷移先URLを取得する
   */
  getTransitionDestinationUrl(code: string): string | null {
    const destination = this._masterData()?.transitionMaster?.find((d) => d.transitionDestinationCode?.trim().toLowerCase() === code.trim().toLowerCase());
    const url = destination?.transitionDestinationUrl?.trim();
    return url && url.length > 0 ? url : null;
  }

  /**
   * メッセージIDに対応するメッセージマスタ情報を取得する。
   */
  getMessageInfo(messageId: string) {
    const message = this._masterData()?.messageMaster?.find((item) => item.messageId === messageId);
    return message ?? null;
  }

  /**
   * メッセージIDに対応するメッセージ本文を取得する。
   */
  getMessageText(messageId: string): string {
    return this.getMessageInfo(messageId)?.messageMainText ?? '';
  }

  /**
   * タイプIDに対応するポートフォリオ情報（初期表示）を全件取得する（FH0021で使用）。
   * 1タイプに対して複数プラン（シンプル・ミックス等）が存在するため、配列で返却する。
   */
  getPortfolioInfoInitialList(typeId: string): PortfolioInfoInitialItem[] {
    return this._masterData()?.portfolioInfoInitial?.filter((p) => p.typeId === typeId) ?? [];
  }

  /**
   * 特別勘定科目リスト（12科目）を取得する（FH0021で使用）
   */
  getSeparateAccountBasicList(): SeparateAccountBasicItem[] {
    return this._masterData()?.separateAccountBasic ?? [];
  }

  /**
   * 用語マスタを WORD_ID 数値昇順で全件取得する（用語解説モーダルで使用）。
   */
  getWordExplanationList(): WordExplanationItem[] {
    return [...(this._masterData()?.wordExplanation ?? [])].sort((a, b) => Number(a.wordId) - Number(b.wordId));
  }

  /**
   * 登録番号（マスタ先頭件）を取得する。未設定時は空文字を返す。
   */
  getRegistrationNo(): string {
    const value = this._masterData()?.registrationNoList?.[0]?.registrationNo;
    return value?.trim() ?? '';
  }

  /**
   * タイプID・モデルポートフォリオIDに対応するポートフォリオ情報（初期表示）を1件取得する（FH0030で使用）。
   */
  getPortfolioInfoInitial(typeId: string, modelPortfolioId: string): PortfolioInfoInitialItem | undefined {
    return this._masterData()?.portfolioInfoInitial?.find((p) => p.typeId === typeId && p.modelPortfolioId === modelPortfolioId);
  }

  /** 商品コード827の販売初期表示区分から、FH0030の初期選択期間を取得する。 */
  getDefaultSalesPeriod(): 'before' | 'after' {
    const product = this._percentageChangeData()?.percentageChangeData?.mProduct?.find((item) => item.productCode === '827');
    const category = product?.salesInitialDisplayCategory;
    return category === '0' ? 'before' : 'after';
  }

  /** 商品コード827の販売開始日（yyyyMMdd）を取得する。 */
  getSalesStartDate(): string | null {
    return this._percentageChangeData()?.percentageChangeData?.mProduct?.find((item) => item.productCode === '827')?.salesStartDate ?? null;
  }

  /**
   * 指定した販売区分（販売前／販売後）の特別勘定詳細データ一覧を取得する（FH0030で使用）。
   *
   * 騰落率データ取得API（FH0003）は「特別勘定コードをキーとする連想配列」かつ
   * 販売前（startBefore）／販売後（startAfter）で項目名が異なる形式で返却されるため、
   * 画面表示しやすい共通の項目名（return1Month等）へ正規化して返却する。
   */
  getSeparateAccountDetailList(salesPeriod: 'before' | 'after'): SeparateAccountDetailItem[] {
    const separateAccount = this._percentageChangeData()?.percentageChangeData?.separateAccount ?? {};

    return Object.entries(separateAccount).map(([separateAccountCode, account]) => {
      const before = account?.startBefore;
      const after = account?.startAfter;

      return {
        separateAccountCode,
        unitPrice: (salesPeriod === 'before' ? before?.netAssetValue : after?.netAssetValue) ?? null,
        return1Month: (salesPeriod === 'before' ? before?.past1MonthBefore : after?.past1MonthAfter) ?? null,
        return3Month: (salesPeriod === 'before' ? before?.past3MonthBefore : after?.past3MonthAfter) ?? null,
        return6Month: (salesPeriod === 'before' ? before?.past6MonthBefore : after?.past6MonthAfter) ?? null,
        return1Year: (salesPeriod === 'before' ? before?.past1YearBefore : after?.past1YearAfter) ?? null,
        return3Year: (salesPeriod === 'before' ? before?.past3YearBefore : after?.past3YearAfter) ?? null,
        return5Year: (salesPeriod === 'before' ? before?.past5YearBefore : after?.past5YearAfter) ?? null,
        returnSinceInception: (salesPeriod === 'before' ? before?.sinceStartBefore : after?.sinceStartAfter) ?? null,
      };
    });
  }

  /**
   * 投資スタイル診断（FH0020）の回答No（Q1～Q4）を保持する。
   * 4問のいずれかが欠けている場合は「未保持」として null 扱いにする。
   */
  setDiagnosisAnswers(answers: Record<number, string>): void {
    const hasAllAnswers = [1, 2, 3, 4].every((n) => {
      const value = answers[n];
      return value !== undefined && value !== null && value !== '';
    });

    if (!hasAllAnswers) {
      this._diagnosisAnswers.set(null);
      this._diagnosisTypeId.set(null);
      return;
    }

    this._diagnosisAnswers.set({
      1: answers[1],
      2: answers[2],
      3: answers[3],
      4: answers[4],
    });
  }

  /**
   * 投資スタイル診断（FH0020）の回答No（Q1～Q4）を取得する。
   * 4問が揃っていない場合は null を返す。
   */
  getDiagnosisAnswers(): Record<number, string> | null {
    const answers = this._diagnosisAnswers();
    if (!answers) {
      return null;
    }
    return { ...answers };
  }

  /** 診断結果取得API（FH0001）で判定したタイプID（"01"/"02"/"03"）を保持する。 */
  setDiagnosisTypeId(typeId: string | null): void {
    if (typeId === '01' || typeId === '02' || typeId === '03') {
      this._diagnosisTypeId.set(typeId);
      return;
    }
    this._diagnosisTypeId.set(null);
  }

  /** 診断結果取得API（FH0001）で判定したタイプID（"01"/"02"/"03"）を取得する。 */
  getDiagnosisTypeId(): string | null {
    return this._diagnosisTypeId();
  }

  /**
   * FH0020の診断完了直後であることを記録する。
   * 次のFH0021初期表示で診断結果タイプタブを優先適用するために使用する。
   */
  markDiagnosisResultTabPending(): void {
    this._pendingDiagnosisResultTab.set(true);
  }

  /**
   * 診断完了直後フラグを取得し、同時に消費する。
   * true の場合はFH0021の初期タブを診断結果タイプとする。
   */
  consumeDiagnosisResultTabPending(): boolean {
    const pending = this._pendingDiagnosisResultTab();
    if (pending) {
      this._pendingDiagnosisResultTab.set(false);
    }
    return pending;
  }

  /** FH0021で選択中だったタイプタブのタイプID（"01"/"02"/"03"）を保持する。 */
  setSelectedTypeTabId(typeId: string | null): void {
    if (typeId === '01' || typeId === '02' || typeId === '03') {
      this._selectedTypeTabId.set(typeId);
      return;
    }
    this._selectedTypeTabId.set(null);
  }

  /** FH0021で選択中だったタイプタブのタイプID（"01"/"02"/"03"）を取得する。 */
  getSelectedTypeTabId(): string | null {
    return this._selectedTypeTabId();
  }

  /** ご留意事項（FH0011）の「同意する」チェック状態を保持する。 */
  setNoticeAgreed(agreed: boolean): void {
    this._noticeAgreed.set(agreed);
  }

  /** ご留意事項（FH0011）の「同意する」チェック状態を取得する。 */
  isNoticeAgreed(): boolean {
    return this._noticeAgreed();
  }

  /**
   * システム日付を取得する
   *
   * マスタ未取得・不正値の場合はブラウザの当日日付を返す。
   *
   * @returns システム日付
   */
  getSystemDate(): Date {
    return parseYyyyMMddToDate(this._masterData()?.systemDate) ?? new Date();
  }

  /**
   * 商品販売開始後かどうかを判定する
   * @returns 販売開始後の場合は true、販売前の場合は false
   */
  getIsSaleStarted(): boolean {
    const salesStartDate = parseYyyyMMddToDate(this.getSalesStartDate());
    const systemDate = this.getSystemDate();
    return salesStartDate ? salesStartDate <= systemDate : false;
  }
  /**
   * 各勘定価額推移算出API（FH0005）を呼び出す（GET方式。「12ファンド＋ポートフォリオ」分の基準価額推移データを取得する）
   */
  fetchPriceTrend(): Observable<Fh0005PriceTrendResponse> {
    const url = `${environment.bffUrl}/fsthp/${environment.apiVersion}/api/fh0005`;
    return this.apiClient.request<Fh0005PriceTrendResponse>(url, 'get', 'fsthp', 'fsthp', '');
  }

  /**
   * 診断結果取得API（FH0001）を呼び出す（POST方式。設問1～4の回答からタイプIDを判定する）
   */
  getDiagnosticResults(request: Fh0001DiagnosticResultsRequest): Observable<Fh0001DiagnosticResultsResponse> {
    const url = `${environment.bffUrl}/fsthp/${environment.apiVersion}/api/fh0001`;
    return this.apiClient.request<Fh0001DiagnosticResultsResponse>(url, 'post', 'fsthp', 'fsthp', '', request);
  }

  /**
   * リスク・リターン算出API（FH0005）を呼び出す（POST方式。ポートフォリオ内訳（5年・10年分の変更後繰入比率）から
   * ポートフォリオのリスク・リターンを算出する。FH0030「入力したポートフォリオの勘定配分を反映」で使用）
   */
  fetchRiskReturn(request: Fh0005RiskReturnRequest): Observable<Fh0005RiskReturnResponse> {
    const url = `${environment.bffUrl}/fsthp/${environment.apiVersion}/api/fh0005`;
    return this.apiClient.request<Fh0005RiskReturnResponse>(url, 'post', 'fsthp', 'fsthp', '', request);
  }

  /**
   * 各勘定価額推移算出API（FH0006）を呼び出す（POST方式。ポートフォリオ内訳（特別勘定コード・繰入比率）から
   * 各特別勘定及びポートフォリオの基準価額伸び率（指数化）の推移を取得する。FH0030で使用）
   */
  fetchValueTrend(request: Fh0006ValueTrendRequest): Observable<Fh0006ValueTrendResponse> {
    const url = `${environment.bffUrl}/fsthp/${environment.apiVersion}/api/fh0006`;
    return this.apiClient.request<Fh0006ValueTrendResponse>(url, 'post', 'fsthp', 'fsthp', '', request);
  }

  /**
   * マスタデータ取得API（FH0004）を呼び出す（GET方式）
   */
  private fetchMasterData(): Observable<Fh0004MasterDataResponse> {
    const url = `${environment.bffUrl}/fsthp/${environment.apiVersion}/api/fh0004`;
    return this.apiClient.request<Fh0004MasterDataResponse>(url, 'get', 'fsthp', 'fsthp', '');
  }

  /**
   * 騰落率データ取得API（FH0003）を呼び出す（GET方式）
   */
  private fetchPercentageChangeData(): Observable<Fh0003PercentageChangeResponse> {
    const url = `${environment.bffUrl}/fsthp/${environment.apiVersion}/api/fh0003`;
    return this.apiClient.request<Fh0003PercentageChangeResponse>(url, 'get', 'fsthp', 'fsthp', '');
  }
}
